import { useEffect, useMemo, useState } from "react"
import { Gauge, RefreshCw, Search, Zap } from "lucide-react"
import { smartoltService, type SmartOLTSpeedProfiles } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { filterRows, formatUpdated } from "./smartoltSettingsUtils.ts"

const empty: SmartOLTSpeedProfiles = { items: [], summary: { total: 0 }, updated_at: null }

function bandwidthBar(value: string | null | undefined, maxMbps = 1000) {
  if (!value) return null
  const num = parseFloat(value)
  if (Number.isNaN(num)) return null
  const pct = Math.min(100, (num / maxMbps) * 100)
  return { pct, num }
}

export function SmartOLTSpeedProfilesPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTSpeedProfiles>(empty)
  const [search, setSearch] = useState("")

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try { await smartoltService.runSyncNow(25) } catch { /* list cached data */ }
      }
      const next = await smartoltService.getSpeedProfiles()
      setData({ items: next?.items || [], summary: next?.summary || empty.summary, updated_at: next?.updated_at || null })
    } catch {
      toast.error("Failed to load speed profiles")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(
    () => filterRows(data.items, search, ["name", "profile_id", "upload", "download"]),
    [data.items, search],
  )

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Speed Profiles</h1>
          <p className="mt-0.5 text-xs text-slate-500">Bandwidth plans used when provisioning ONUs</p>
        </div>
        <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">
          {data.summary.total} profiles
        </span>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/50 p-3">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search profiles…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20" />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load(true, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync from SmartOLT
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading speed profiles…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Gauge className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">No speed profiles yet</p>
            <p className="mt-1 text-xs text-slate-500">Profiles sync from SmartOLT when traffic profiles are configured on the OLT.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {["Profile", "Download", "Upload", "Synced"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => {
                  const dl = bandwidthBar(row.download)
                  const ul = bandwidthBar(row.upload)
                  return (
                    <tr key={row.profile_id} className="transition-colors hover:bg-slate-50">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50">
                            <Zap className="h-3.5 w-3.5 text-amber-600" />
                          </div>
                          <span className="font-medium text-slate-800">{row.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="space-y-1">
                          <span className="text-sm font-semibold text-emerald-700">{row.download || "—"}</span>
                          {dl && (
                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${dl.pct}%` }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="space-y-1">
                          <span className="text-sm font-semibold text-blue-700">{row.upload || "—"}</span>
                          {ul && (
                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full bg-blue-500 transition-all" style={{ width: `${ul.pct}%` }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-slate-500">{formatUpdated(row.fetched_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
